// OfflineBanner.tsx — pysyvä yläbanneri kun yhteys on poikki + queue-counter.
//
// Näytetään kahdessa tapauksessa:
// 1. Offline-tila: keltainen banneri + queue-counter
// 2. Online + queue ei tyhjä: sininen banneri "Synkronoidaan X muutosta..."

import { useEffect, useState } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useQueueCount } from '../hooks/useQueueCount';

export default function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const queued = useQueueCount();
  // 10.7.2026: totuudenmukainen tila — jos synkka on jo yrittänyt eikä onnistunut,
  // ei väitetä ikuisesti "Synkronoidaan…". Hylätyistä kerrotaan hetken ajan.
  const [lastSync, setLastSync] = useState<{ failed: number; dropped: number; authBlocked: boolean } | null>(null);
  const [droppedNotice, setDroppedNotice] = useState<number>(0);
  useEffect(() => {
    const onSynced = (e: Event) => {
      const d = (e as CustomEvent).detail ?? {};
      setLastSync({ failed: d.failed ?? 0, dropped: d.dropped ?? 0, authBlocked: d.authBlocked ?? false });
      if ((d.dropped ?? 0) > 0) {
        setDroppedNotice(d.dropped);
        setTimeout(() => setDroppedNotice(0), 12_000);
      }
    };
    window.addEventListener('queue:synced', onSynced);
    return () => window.removeEventListener('queue:synced', onSynced);
  }, []);

  // Hylkäysilmoitus näytetään vaikka jono olisi tyhjä
  if (droppedNotice > 0 && queued === 0) {
    return (
      <div role="status" aria-live="polite" style={{ ...bannerBase, ...offlineStyle }}>
        <span style={{ fontSize: 16 }}>⚠️</span>
        <span>
          {droppedNotice} {droppedNotice === 1 ? 'muutosta' : 'muutosta'} ei voitu synkronoida ja se hylättiin — tarkista viimeisimmät kirjauksesi.
        </span>
      </div>
    );
  }

  // Jos online + queue tyhjä, ei näytetä mitään
  if (isOnline && queued === 0) return null;

  // Online + queueessa odottavia → synkronointi käynnissä / odottaa
  if (isOnline && queued > 0) {
    const waitingAuth = lastSync?.authBlocked === true;
    const stalled = !waitingAuth && (lastSync?.failed ?? 0) > 0;
    return (
      <div role="status" aria-live="polite" style={{ ...bannerBase, ...syncingStyle }}>
        <span style={{ fontSize: 16 }}>🔄</span>
        <span>
          {waitingAuth
            ? `${queued} ${queued === 1 ? 'muutos' : 'muutosta'} odottaa kirjautumista — avaa sovellus/kirjaudu uudelleen`
            : stalled
              ? `${queued} ${queued === 1 ? 'muutos' : 'muutosta'} jonossa — yritetään uudelleen hetken kuluttua`
              : `Synkronoidaan ${queued} ${queued === 1 ? 'muutos' : 'muutosta'}…`}
        </span>
      </div>
    );
  }

  // Offline
  return (
    <div role="status" aria-live="polite" style={{ ...bannerBase, ...offlineStyle }}>
      <span style={{ fontSize: 16 }}>⚠️</span>
      <span>
        Ei verkkoyhteyttä —{' '}
        {queued > 0
          ? `${queued} ${queued === 1 ? 'muutos' : 'muutosta'} jonossa, synkronoidaan kun yhteys palaa`
          : 'muutokset tallennetaan ja synkronoidaan kun yhteys palaa'}
      </span>
    </div>
  );
}

const bannerBase: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  padding: '10px 16px',
  fontSize: 13,
  fontWeight: 600,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))',
};

const offlineStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)',
  borderBottom: '1px solid #f59e0b',
  color: '#78350f',
  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
};

const syncingStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%)',
  borderBottom: '1px solid #3b82f6',
  color: '#1e3a8a',
  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
};
